import { useCallback, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  deleteCompensationAttachment,
  fetchArchiveRequests,
  fetchCurrentYearRequests,
  submitCompensationAttachments,
  updateManagementCompensation,
  updateUserCompensation,
} from 'services';
import { useAppDispatch, useNotification } from 'hooks';
import { Attachment, EditableCompensation } from 'types';
import { Path } from 'enums';

export const useSubmitRequest = (
  onSuccessSubmit: () => void,
  isCurrentUserCompensation: boolean,
  attachments: Attachment[],
) => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const sendNotification = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const returnTo = typeof state === 'string' ? state : './../';

  const handleSubmit = useCallback(
    async (updatedCompensation: EditableCompensation) => {
      setLoading(true);

      try {
        unwrapResult(
          await dispatch(
            isCurrentUserCompensation
              ? updateUserCompensation(updatedCompensation)
              : updateManagementCompensation(updatedCompensation),
          ),
        );

        if (updatedCompensation.attachments.length !== attachments.length) {
          const removedAttachments = attachments
            .filter(
              (item) => !updatedCompensation.attachments.some(({ attachmentID }) => attachmentID === item.attachmentID),
            )
            .map(({ attachmentID }) =>
              dispatch(
                deleteCompensationAttachment({
                  compensationRequestID: updatedCompensation.compensationRequestID,
                  attachmentID,
                }),
              ),
            );

          await Promise.all(removedAttachments);
        }

        if (updatedCompensation.newAttachments?.length) {
          await dispatch(
            submitCompensationAttachments({
              compensationRequestID: updatedCompensation.compensationRequestID,
              files: updatedCompensation.newAttachments,
            }),
          );
        }

        setLoading(false);
        sendNotification(t('success'), 'success');

        if (!isCurrentUserCompensation) {
          const fetchRequests = returnTo.includes(`/${Path.Archive}`) ? fetchArchiveRequests : fetchCurrentYearRequests;

          dispatch(fetchRequests());
        }

        navigate(returnTo);
        onSuccessSubmit();
      } catch (e) {
        setLoading(false);
      }
    },
    [dispatch, isCurrentUserCompensation, attachments, sendNotification, t, navigate, returnTo, onSuccessSubmit],
  );

  return { handleSubmit, isLoading };
};
